import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { studioProjects, studioDeployments, studioSecurity } from '@/lib/api';

interface Project {
  id: string;
  name: string;
  status: string;
  client_count: number;
  deployment_id?: string | null;
  last_deployed_at?: string | null;
  branding: Record<string, unknown>;
}

interface Deployment {
  deployment_id: string;
  subdomain: string;
  url: string;
  ssl_status: string;
  deploy_status: string;
  deployed_at?: string | null;
  security_baseline_passed: boolean;
}

export function AppDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [project, setProject] = useState<Project | null>(null);
  const [deployment, setDeployment] = useState<Deployment | null>(null);
  const [securityScore, setSecurityScore] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [deploying, setDeploying] = useState(false);

  useEffect(() => {
    if (!id) return;
    async function load() {
      try {
        const [p, sec] = await Promise.allSettled([
          studioProjects.get(id!),
          studioSecurity.posture(id!),
        ]);

        if (p.status === 'fulfilled') {
          const proj = p.value as Project;
          setProject(proj);
          if (proj.deployment_id) {
            const dep = await studioDeployments.status(proj.deployment_id);
            setDeployment(dep as Deployment);
          }
        }
        if (sec.status === 'fulfilled') {
          setSecurityScore((sec.value as { score: number }).score ?? null);
        }
      } finally {
        setLoading(false);
      }
    }
    void load();
  }, [id]);

  async function handleDeploy() {
    if (!id) return;
    setDeploying(true);
    try {
      await studioProjects.deploy(id);
      navigate(0); // refresh
    } finally {
      setDeploying(false);
    }
  }

  if (loading) return <div className="p-8 text-muted-foreground">Loading…</div>;
  if (!project) return <div className="p-8 text-muted-foreground">App not found.</div>;

  return (
    <div className="p-8 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <div>
          <button onClick={() => navigate('/apps')} className="text-sm text-muted-foreground hover:text-foreground mb-2">
            ← My Apps
          </button>
          <h1 className="text-2xl font-bold text-foreground">{project.name}</h1>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => navigate(`/builder/${project.id}`)}
            className="border border-border px-4 py-2 rounded-lg text-sm"
          >
            Open Builder
          </button>
          {project.status !== 'deployed' && (
            <button
              onClick={() => void handleDeploy()}
              disabled={deploying}
              className="bg-primary text-primary-foreground px-4 py-2 rounded-lg text-sm disabled:opacity-50"
            >
              {deploying ? 'Deploying…' : 'Deploy'}
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="border border-border rounded-xl p-4 bg-card">
          <p className="text-xs text-muted-foreground mb-1">Status</p>
          <span className={`text-sm font-medium ${project.status === 'deployed' ? 'text-green-600' : 'text-muted-foreground'}`}>
            {project.status}
          </span>
        </div>
        <div className="border border-border rounded-xl p-4 bg-card">
          <p className="text-xs text-muted-foreground mb-1">Clients</p>
          <p className="text-sm font-medium text-foreground">{project.client_count}</p>
        </div>
        <div className="border border-border rounded-xl p-4 bg-card">
          <p className="text-xs text-muted-foreground mb-1">Security Score</p>
          <p className={`text-sm font-medium ${securityScore !== null && securityScore >= 80 ? 'text-green-600' : 'text-amber-600'}`}>
            {securityScore !== null ? `${securityScore}/100` : '—'}
          </p>
        </div>
      </div>

      {deployment && (
        <div className="border border-border rounded-xl p-5 bg-card mb-4">
          <h2 className="text-sm font-semibold text-foreground mb-3">Deployment</h2>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">URL</span>
              <a href={deployment.url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                {deployment.url}
              </a>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">SSL</span>
              <span className={deployment.ssl_status === 'active' ? 'text-green-600' : 'text-amber-600'}>
                {deployment.ssl_status}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
